import { useEffect, useRef, useState } from 'react';
import Script from 'next/script';
import { Session } from 'next-auth';
import { useSession } from 'next-auth/react';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import { insecureHash, isAnonymousUser, isTestUser } from 'lib/auth/client-utils';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { PageLoadDataLayerObject } from 'lib/util/gtm-datalayer/_data-layer-types';
import { gtmDatalayerPush } from 'lib/util/gtm-datalayer';
import useSafeSession from 'lib/util/useSafeSession';

/**
 * Properties for the Google Tag Manager component.
 */
export type GTMProps = {
  fields?: {
    /**
     * Comma-delimited list of google tag identifiers.
     */
    gt?: string;
    /**
     * WalkMe client id
     */
    wmI?: string;
    /**
     * WalkMe in test mode?
     */
    wmT: boolean;
  };
};

/**
 * Extends the global `Window` interface to include an optional `__walkMeUser` property.
 * This property is used to store session information for WalkMe.
 *
 * @interface WindowWithWalkMe
 * @extends {Window}
 * @property {Session} [__walkMeUser] - Optional property to store WalkMe session information.
 */
interface WindowWithWalkMe extends Window {
  __walkMeUser?: Session;
}

/**
 * WalkMeSnippet component integrates WalkMe script into the application.
 * It conditionally adds the WalkMe script based on the provided fields and session data.
 *
 * @param {GTMProps} fields - The properties passed to the component.
 * @returns {JSX.Element} The JSX element containing the WalkMe script or an empty fragment.
 *
 * @example
 * <WalkMeSnippet fields={fields} />
 *
 * @remarks
 * - The component uses the `useSession` hook to get the session data.
 * - It checks if the user is not anonymous and not a test user before adding the script.
 * - The WalkMe script is added only if the `walkMeId` is present and the component is mounted.
 *
 * @see {@link https://cdn.walkme.com/} for more information about WalkMe.
 */
const WalkMeSnippet = ({ fields }: GTMProps) => {
  const { data: session } = useSession();
  const [isMounted, setIsMounted] = useState<boolean>(false);
  useEffect(() => {
    if (!isMounted) {
      if (session !== null && !isAnonymousUser(session?.authz_site) && !isTestUser(session?.user)) {
        if (typeof window !== 'undefined') {
          (window as WindowWithWalkMe).__walkMeUser = session;
        }
        setIsMounted(true);
      }
    }
  }, [isMounted, session]);
  const walkMeId = (fields?.wmI ?? '').trim();
  const shouldAddScript = walkMeId.length > 0 && isMounted;
  const walkMeTest = fields?.wmT ?? true ? `/test` : ``;
  return shouldAddScript ? (
    <Script id="nmdp-snippets-walkme" strategy="afterInteractive">
      {`(function(){var walkme=document.createElement('script');walkme.type='text/javascript';walkme.async=true;` +
        `walkme.src='https://cdn.walkme.com/users/${walkMeId}${walkMeTest}/walkme_${walkMeId}_https.js';var s=document.getElementsByTagName('script')[0];` +
        `s.parentNode.insertBefore(walkme, s);window._walkmeConfig={smartLoad:true};})();`}
    </Script>
  ) : (
    <></>
  );
};

/**
 * The `Gtm` component integrates Google Tag Manager (GTM) into the application.
 * It conditionally loads GTM scripts based on the environment and user session.
 *
 * @param {GTMProps} fields - The fields containing GTM configuration.
 * @returns {React.JSX.Element} The rendered GTM scripts and noscript tags.
 *
 * @remarks
 * - If running in the Sitecore Experience Editor, the component renders nothing.
 * - If no GTM tags are configured, it only renders the `WalkMeSnippet` component.
 * - If GTM tags are configured, it dynamically injects the GTM scripts and noscript tags.
 *
 * @example
 * ```tsx
 * <Gtm fields={fields} />
 * ```
 */
const Gtm = ({ fields }: GTMProps): React.JSX.Element => {
  // If we are running in experience editor we want to do nothing
  const isExperienceEditor = useExperienceEditor();
  const context = useSitecoreContext();
  const { gtmPageType, itemPath } = context.sitecoreContext;
  const { data: session, status } = useSafeSession();
  const noScriptRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (
      noScriptRef.current &&
      gtmPageType &&
      (status === 'authenticated' || status === 'disabled')
    ) {
      const hashPromise = isAnonymousUser(session?.authz_site)
        ? Promise.resolve(undefined)
        : insecureHash(session?.user?.name ?? '');
      hashPromise.then((hash) => {
        if (noScriptRef.current) {
          gtmDatalayerPush({
            event: 'pageLoad',
            pageType: gtmPageType,
            userId: hash,
          } as PageLoadDataLayerObject);
        }
      });
    }
  }, [gtmPageType, itemPath, session, status]);
  if (isExperienceEditor) {
    return <></>;
  }

  // Read configured container tags from page fields
  const skipAuthentication = process.env.SKIP_AUTHENTICATION === 'true';
  const tag = (fields?.gt ?? '').trim();
  // Allow for multiple comma-delimited tags
  const tags = tag
    .split(',')
    .map((x) => (x || '').trim())
    .filter((x) => x.length > 0);
  const walkMeSnippet = skipAuthentication ? <></> : <WalkMeSnippet fields={fields} />;
  // If no tags configured then nothing to do
  if (tags.length === 0) {
    return walkMeSnippet;
  }
  // Otherwise add the javascript and frames versions of the container to the page
  return (
    <>
      {tags.map((item) => {
        const tagId = `nmdp-snippets-gtm-${item}`;
        return (
          <Script id={tagId} key={tagId} strategy="afterInteractive">
            {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],` +
              `j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;` +
              `f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${item}');`}
          </Script>
        );
      })}
      <noscript ref={noScriptRef}>
        {tags.map((item) => (
          <iframe
            key={item}
            src={`https://www.googletagmanager.com/ns.html?id=${item}`}
            height="0"
            width="0"
            title="gtm"
            className="hidden"
          />
        ))}
      </noscript>
      {walkMeSnippet}
    </>
  );
};

export default Gtm;
